import React from 'react';

import {SaveStatus} from '../../pages/reportBuilder/constants/ReportBuilderInterfce';
import useDocumentStore from '../../store/reportStore/documentStore';

/**
 * SaveStatusIndicator
 *
 * Shows the current document save state in the editor header/toolbar.
 * Displays nothing while 'idle', a pulsing dot when 'saving',
 * amber "Unsaved changes" when 'modified', a green checkmark when 'saved',
 * and a red warning on 'error'.
 */
const SaveStatusIndicator: React.FC = () => {
  const status: SaveStatus = useDocumentStore((s) => s.saveStatus) ?? 'idle';

  if (status === 'idle') return null;

  const configs: Record<
    Exclude<SaveStatus, 'idle'>,
    {label: string; color: string; bg: string; pulse: boolean}
  > = {
    saving: {label: 'Saving…', color: '#1a73e8', bg: '#eff6ff', pulse: true},
    saved: {label: '✓ Saved', color: '#16a34a', bg: '#f0fdf4', pulse: false},
    modified: {
      label: 'Unsaved changes',
      color: '#b45309',
      bg: '#fffbeb',
      pulse: false
    },
    error: {
      label: '⚠ Save failed',
      color: '#dc2626',
      bg: '#fef2f2',
      pulse: false
    }
  };

  const cfg = configs[status as Exclude<SaveStatus, 'idle'>];
  if (!cfg) return null;

  return (
    <span
      className="save-status-indicator"
      title={
        status === 'error'
          ? 'Could not reach server. Changes may not be saved.'
          : undefined
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: '\'Inter\', \'Segoe UI\', sans-serif',
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}33`,
        userSelect: 'none',
        transition: 'all 0.2s ease'
      }}
    >
      {cfg.pulse && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: cfg.color,
            display: 'inline-block',
            animation: 'esg-pulse 1s ease-in-out infinite'
          }}
        />
      )}
      {cfg.label}

      {/* Keyframes injected once */}
      <style>{`
        @keyframes esg-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
      `}</style>
    </span>
  );
};

export default SaveStatusIndicator;
